import { normalizeDisplayLabelWithoutUndefined } from "../../utility/displayText";
export interface ChartDataParams {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  sortby?: string[];
  reverseSort?: string[];
  priceKey?: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
  average?: boolean;
  averageKeys?: string[]; // New property for keys to average
  averageGroupKeys?: string[]; // New property for keys to group averages by
  averageDuplicationKeys?: string[];
  tableKeys?: string[];
}

export interface ChartData {
  keys: string[];
  unit_keys: string[];
  keysLabels: string[];
  group_by: Record<string, any[]>;
  data: Array<Record<string, any>>;
  tableData?: Array<Record<string, any>>;
}

type DataRow = Record<string, any>;

function toFiniteNumber(value: any): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getBucketNumber(bucket: DataRow, field: string): number {
  const value = bucket[field];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function incrementBucketField(
  bucket: DataRow,
  field: string,
  incrementBy: number,
) {
  bucket[field] = getBucketNumber(bucket, field) + incrementBy;
}

function buildCompositeKey(row: DataRow, fields: string[]): string {
  const fieldCount = fields.length;
  if (fieldCount === 0) {
    return "";
  }

  if (fieldCount === 1) {
    return String(row[fields[0]]);
  }

  const parts = new Array<string>(fieldCount);
  for (let index = 0; index < fieldCount; index += 1) {
    parts[index] = String(row[fields[index]]);
  }

  return parts.join("_");
}

function areFieldListsEqual(a: string[], b?: string[]): boolean {
  if (!b || a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) {
      return false;
    }
  }

  return true;
}

function collectGroupInfo(
  data: DataRow[],
  groupBy: string[],
): {
  distinctGroupCombinations: string[];
  groupByKey: Record<string, any[]>;
  groupKeysByRow: string[];
} {
  const distinctGroupCombinations: string[] = [];
  const seenGroupCombinations = new Set<string>();
  const groupKeysByRow = new Array<string>(data.length);
  const groupBySets: Record<string, Set<any>> = {};

  for (const key of groupBy) {
    groupBySets[key] = new Set<any>();
  }

  for (let rowIndex = 0; rowIndex < data.length; rowIndex += 1) {
    const row = data[rowIndex];
    const groupKey = buildCompositeKey(row, groupBy);
    groupKeysByRow[rowIndex] = groupKey;

    if (!seenGroupCombinations.has(groupKey)) {
      seenGroupCombinations.add(groupKey);
      distinctGroupCombinations.push(groupKey);
    }

    for (const key of groupBy) {
      groupBySets[key].add(row[key]);
    }
  }

  const groupByKey: Record<string, any[]> = {};
  for (const key of groupBy) {
    groupByKey[key] = Array.from(groupBySets[key]);
  }

  // Globally stabilize the order of group combinations so that index-based colors stay consistent
  distinctGroupCombinations.sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }),
  );

  return {
    distinctGroupCombinations,
    groupByKey,
    groupKeysByRow,
  };
}

function sortRows(rows: DataRow[], sortby?: string[], reverseSort?: string[]) {
  const descendingKeys = sortby ?? [];
  const ascendingKeys = reverseSort ?? [];

  if (descendingKeys.length === 0 && ascendingKeys.length === 0) {
    return;
  }

  rows.sort((a, b) => {
    for (const key of descendingKeys) {
      if (a[key] > b[key]) return -1;
      if (a[key] < b[key]) return 1;
    }

    for (const key of ascendingKeys) {
      if (a[key] > b[key]) return 1;
      if (a[key] < b[key]) return -1;
    }

    return 0;
  });
}

interface GroupFieldTemplate {
  groupKey: string;
  volumeField: string;
  priceField: string;
  aggregationFields: string[];
  averageFields: string[];
  averageCountFields: string[];
  averageSetFields: string[];
}

function buildGroupFieldTemplates(
  distinctGroupCombinations: string[],
  aggregationKeys: string[],
  averageKeys: string[],
  priceKey: string,
  volumeKey: string,
): {
  orderedTemplates: GroupFieldTemplate[];
  templatesByGroup: Map<string, GroupFieldTemplate>;
} {
  const orderedTemplates = new Array<GroupFieldTemplate>(
    distinctGroupCombinations.length,
  );
  const templatesByGroup = new Map<string, GroupFieldTemplate>();

  for (let index = 0; index < distinctGroupCombinations.length; index += 1) {
    const groupKey = distinctGroupCombinations[index];
    const prefix = `${groupKey}_`;

    const aggregationFields = new Array<string>(aggregationKeys.length);
    for (let aggIndex = 0; aggIndex < aggregationKeys.length; aggIndex += 1) {
      aggregationFields[aggIndex] = `${prefix}${aggregationKeys[aggIndex]}`;
    }

    const averageFields = new Array<string>(averageKeys.length);
    const averageCountFields = new Array<string>(averageKeys.length);
    const averageSetFields = new Array<string>(averageKeys.length);
    for (let avgIndex = 0; avgIndex < averageKeys.length; avgIndex += 1) {
      const avgField = `${prefix}${averageKeys[avgIndex]}`;
      averageFields[avgIndex] = avgField;
      averageCountFields[avgIndex] = `${avgField}_count`;
      averageSetFields[avgIndex] = `${avgField}_set`;
    }

    const template: GroupFieldTemplate = {
      groupKey,
      volumeField: `${prefix}${volumeKey}`,
      priceField: `${prefix}${priceKey}`,
      aggregationFields,
      averageFields,
      averageCountFields,
      averageSetFields,
    };

    orderedTemplates[index] = template;
    templatesByGroup.set(groupKey, template);
  }

  return {
    orderedTemplates,
    templatesByGroup,
  };
}

interface AggregateRowsArgs {
  data: DataRow[];
  keyFields: string[];
  groupBy: string[];
  groupKeysByRow?: string[];
  distinctGroupCombinations: string[];
  aggregationKeys: string[];
  averageKeys: string[];
  averageDuplicationKeys?: string[];
  priceKey: string;
  volumeKey: string;
}

function aggregateRows({
  data,
  keyFields,
  groupBy,
  groupKeysByRow,
  distinctGroupCombinations,
  aggregationKeys,
  averageKeys,
  averageDuplicationKeys,
  priceKey,
  volumeKey,
}: AggregateRowsArgs): DataRow[] {
  const rowsByKey = new Map<string, DataRow>();
  const hasAverageKeys = averageKeys.length > 0;
  const averageDuplicationKeyList = averageDuplicationKeys ?? [];
  const hasAverageDuplicationKeys = averageDuplicationKeyList.length > 0;
  const { orderedTemplates, templatesByGroup } = buildGroupFieldTemplates(
    distinctGroupCombinations,
    aggregationKeys,
    averageKeys,
    priceKey,
    volumeKey,
  );

  for (let rowIndex = 0; rowIndex < data.length; rowIndex += 1) {
    const row = data[rowIndex];
    const rowKey = buildCompositeKey(row, keyFields);
    const groupKey = groupKeysByRow
      ? groupKeysByRow[rowIndex]
      : buildCompositeKey(row, groupBy);
    const groupTemplate = templatesByGroup.get(groupKey);
    if (!groupTemplate) {
      continue;
    }

    let bucket = rowsByKey.get(rowKey);
    if (!bucket) {
      bucket = {} as DataRow;
      for (const field of keyFields) {
        bucket[field] = row[field];
      }

      rowsByKey.set(rowKey, bucket);
    }

    for (let aggIndex = 0; aggIndex < aggregationKeys.length; aggIndex += 1) {
      const aggValue = toFiniteNumber(row[aggregationKeys[aggIndex]]);
      if (aggValue !== null) {
        incrementBucketField(
          bucket,
          groupTemplate.aggregationFields[aggIndex],
          aggValue,
        );
      }
    }

    const volumeValue = toFiniteNumber(row[volumeKey]);
    if (volumeValue !== null) {
      incrementBucketField(bucket, groupTemplate.volumeField, volumeValue);

      const priceValue = toFiniteNumber(row[priceKey]);
      if (priceValue !== null) {
        incrementBucketField(
          bucket,
          groupTemplate.priceField,
          priceValue * volumeValue,
        );
      }
    }

    if (hasAverageKeys) {
      const averageDuplicationSuffix = hasAverageDuplicationKeys
        ? buildCompositeKey(row, averageDuplicationKeyList)
        : "";

      for (let avgIndex = 0; avgIndex < averageKeys.length; avgIndex += 1) {
        const avgValue = toFiniteNumber(row[averageKeys[avgIndex]]);
        if (avgValue === null) {
          continue;
        }

        const avgField = groupTemplate.averageFields[avgIndex];
        const avgCountField = groupTemplate.averageCountFields[avgIndex];
        incrementBucketField(bucket, avgField, avgValue);
        incrementBucketField(bucket, avgCountField, 1);

        if (hasAverageDuplicationKeys) {
          const setField = groupTemplate.averageSetFields[avgIndex];
          let dedupeSet = bucket[setField] as Set<string> | undefined;
          if (!dedupeSet) {
            dedupeSet = new Set<string>();
            bucket[setField] = dedupeSet;
          }

          dedupeSet.add(`${avgField}_${averageDuplicationSuffix}`);
        }
      }
    }
  }

  const result = Array.from(rowsByKey.values());

  for (const bucket of result) {
    for (const groupTemplate of orderedTemplates) {
      const volume = getBucketNumber(bucket, groupTemplate.volumeField);
      bucket[groupTemplate.volumeField] = volume;
      const totalPrice = getBucketNumber(bucket, groupTemplate.priceField);

      if (volume > 0) {
        bucket[groupTemplate.priceField] = totalPrice / volume;
      } else {
        bucket[groupTemplate.priceField] = 0;
      }

      for (const aggregationField of groupTemplate.aggregationFields) {
        bucket[aggregationField] = getBucketNumber(bucket, aggregationField);
      }

      if (hasAverageKeys) {
        for (let avgIndex = 0; avgIndex < averageKeys.length; avgIndex += 1) {
          const avgField = groupTemplate.averageFields[avgIndex];
          const avgCountField = groupTemplate.averageCountFields[avgIndex];
          const divisor = hasAverageDuplicationKeys
            ? ((
                bucket[groupTemplate.averageSetFields[avgIndex]] as
                  | Set<string>
                  | undefined
              )?.size ?? 0)
            : getBucketNumber(bucket, avgCountField);

          const averageTotal = getBucketNumber(bucket, avgField);
          bucket[avgCountField] = getBucketNumber(bucket, avgCountField);

          bucket[avgField] = divisor > 0 ? averageTotal / divisor : 0;
        }
      }
    }
  }

  return result;
}

export function FormatChartData({
  aggregationKeys,
  averageKeys,
  averageDuplicationKeys,
  groupBy,
  keys,
  priceKey = "",
  volumeKey,
  tableKeys,
  sortby,
  reverseSort,
  data,
}: ChartDataParams): ChartData {
  const averageKeyList = averageKeys ?? [];
  const averageKeySet = new Set(averageKeyList);
  const filteredAggregationKeys = aggregationKeys.filter(
    (key) => !averageKeySet.has(key),
  );
  const { distinctGroupCombinations, groupByKey, groupKeysByRow } =
    collectGroupInfo(data, groupBy);

  const units: string[] = [
    priceKey,
    volumeKey,
    ...filteredAggregationKeys,
    ...averageKeyList,
  ];

  const finalRes = aggregateRows({
    data,
    keyFields: keys,
    groupBy,
    groupKeysByRow,
    distinctGroupCombinations,
    aggregationKeys: filteredAggregationKeys,
    averageKeys: averageKeyList,
    averageDuplicationKeys,
    priceKey,
    volumeKey,
  });

  sortRows(finalRes, sortby, reverseSort);

  let tableData: Array<Record<string, any>> = [];

  if (tableKeys && tableKeys.length > 0) {
    if (areFieldListsEqual(keys, tableKeys)) {
      tableData = finalRes.slice();
    } else {
      tableData = aggregateRows({
        data,
        keyFields: tableKeys,
        groupBy,
        groupKeysByRow,
        distinctGroupCombinations,
        aggregationKeys: filteredAggregationKeys,
        averageKeys: averageKeyList,
        averageDuplicationKeys,
        priceKey,
        volumeKey,
      });

      sortRows(tableData, sortby, reverseSort);
    }
  }

  return {
    keys: distinctGroupCombinations,
    unit_keys: units,
    keysLabels: distinctGroupCombinations.map((key) =>
      normalizeDisplayLabelWithoutUndefined(key),
    ),
    group_by: groupByKey,
    data: finalRes,
    tableData: tableData,
  };
}

export function FormatChartDataWithAverages({
  aggregationKeys,
  groupBy,
  keys,
  priceKey,
  volumeKey,
  data,
}: {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  priceKey: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
}): {
  keys: string[];
  keysLabels: string[];
  unit_keys: string[];
  group_by: Record<string, any[]>;
  data: Array<Record<string, any>>;
} {
  const keyData = new Map<string, DataRow>();
  const { distinctGroupCombinations, groupByKey, groupKeysByRow } =
    collectGroupInfo(data, groupBy);
  const units: string[] = [priceKey, volumeKey, ...aggregationKeys];

  const groupTemplates = new Array<{
    groupKey: string;
    volumeField: string;
    priceField: string;
    volumeCountField: string;
    aggregationFields: string[];
  }>(distinctGroupCombinations.length);
  const groupTemplatesByKey = new Map<
    string,
    {
      groupKey: string;
      volumeField: string;
      priceField: string;
      volumeCountField: string;
      aggregationFields: string[];
    }
  >();

  for (let index = 0; index < distinctGroupCombinations.length; index += 1) {
    const groupKey = distinctGroupCombinations[index];
    const prefix = `${groupKey}_`;
    const aggregationFields = new Array<string>(aggregationKeys.length);

    for (let aggIndex = 0; aggIndex < aggregationKeys.length; aggIndex += 1) {
      aggregationFields[aggIndex] = `${prefix}${aggregationKeys[aggIndex]}`;
    }

    const template = {
      groupKey,
      volumeField: `${prefix}${volumeKey}`,
      priceField: `${prefix}${priceKey}`,
      volumeCountField: `${prefix}${volumeKey}_count`,
      aggregationFields,
    };

    groupTemplates[index] = template;
    groupTemplatesByKey.set(groupKey, template);
  }

  for (let rowIndex = 0; rowIndex < data.length; rowIndex += 1) {
    const row = data[rowIndex];
    const rowKey = buildCompositeKey(row, keys);
    const groupKey = groupKeysByRow[rowIndex];
    const groupTemplate = groupTemplatesByKey.get(groupKey);
    if (!groupTemplate) {
      continue;
    }

    let bucket = keyData.get(rowKey);
    if (!bucket) {
      bucket = {} as DataRow;
      for (const field of keys) {
        bucket[field] = row[field];
      }

      keyData.set(rowKey, bucket);
    }

    for (let aggIndex = 0; aggIndex < aggregationKeys.length; aggIndex += 1) {
      const aggValue = toFiniteNumber(row[aggregationKeys[aggIndex]]);
      if (aggValue !== null) {
        incrementBucketField(
          bucket,
          groupTemplate.aggregationFields[aggIndex],
          aggValue,
        );
      }
    }

    const volumeValue = toFiniteNumber(row[volumeKey]);
    if (volumeValue !== null) {
      incrementBucketField(bucket, groupTemplate.volumeField, volumeValue);
      incrementBucketField(bucket, groupTemplate.volumeCountField, 1);

      const priceValue = toFiniteNumber(row[priceKey]);
      if (priceValue !== null) {
        incrementBucketField(
          bucket,
          groupTemplate.priceField,
          priceValue * volumeValue,
        );
      }
    }
  }

  const finalRes: Array<Record<string, any>> = Array.from(keyData.values());
  for (const res of finalRes) {
    for (const groupTemplate of groupTemplates) {
      const count = getBucketNumber(res, groupTemplate.volumeCountField);
      const totalVolume = getBucketNumber(res, groupTemplate.volumeField);
      const averageVolume = count > 0 ? totalVolume / count : 0;

      res[groupTemplate.volumeCountField] = count;
      res[groupTemplate.volumeField] = averageVolume;

      for (const aggregationField of groupTemplate.aggregationFields) {
        const totalAggregation = getBucketNumber(res, aggregationField);
        res[aggregationField] = count > 0 ? totalAggregation / count : 0;
      }

      const totalPrice = getBucketNumber(res, groupTemplate.priceField);
      if (averageVolume > 0) {
        res[groupTemplate.priceField] = totalPrice / averageVolume;
      } else {
        res[groupTemplate.priceField] = 0;
      }
    }
  }

  return {
    keys: distinctGroupCombinations,
    keysLabels: distinctGroupCombinations.map((key) =>
      normalizeDisplayLabelWithoutUndefined(key),
    ),
    unit_keys: units,
    group_by: groupByKey,
    data: finalRes,
  };
}

export function ProcessChartData({
  aggregationKeys,
  groupBy,
  keys,
  priceKey,
  volumeKey,
  data,
  average = false,
}: {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  priceKey: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
  average?: boolean;
}): {
  keys: string[];
  keysLabels: string[];
  unit_keys: string[];
  group_by: Record<string, any[]>;

  data: Array<Record<string, any>>;
} {
  if (average) {
    return FormatChartDataWithAverages({
      aggregationKeys,
      groupBy,
      keys,
      priceKey,
      volumeKey,
      data,
    });
  } else {
    return FormatChartData({
      aggregationKeys,
      groupBy,
      keys,
      priceKey,
      volumeKey,
      data,
    });
  }
}
